import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import {
  selectedSessionIdsAtom,
  useSessionsList,
} from '@/components/layout/Navigation/sessions/side-nav-list/hooks/useSessionsList';
import { TrackedButton } from '@/components/ui/tracked-button';
import { useMutateDeleteEmptySessions } from '@/hooks/mutation/useMutateDeleteEmptySessions';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { useTidyUpRecordingLock } from '@/hooks/useTidyUpRecordingLock';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useAtomValue } from 'jotai';
import { Wand2 } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export const TidyUpButton = () => {
  const { mutateAsync: onDeleteEmptySessions } = useMutateDeleteEmptySessions();
  const { isLocked, lockedReason } = useTidyUpRecordingLock();
  const { sessions, onSessionDeleted } = useSessionsList();

  const isMobile = useIsMobile();

  const isReviewingOtherUser = useIsReviewingOtherUser();

  const onDeleteEmptySessionClick = async () => {
    await onDeleteEmptySessions();
    onSessionDeleted();
  };

  const selectedSessionIds = useAtomValue(selectedSessionIdsAtom);
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();

  const showTidyUpButton = useMemo(() => {
    return (
      selectedSessionIds.length === 0 &&
      !!sessions?.length &&
      sessions?.length > 0 &&
      !isMobile &&
      !isReviewingOtherUser
    );
  }, [selectedSessionIds, sessions, isMobile, isReviewingOtherUser]);

  if (!showTidyUpButton) return <></>;

  const button = (
    <TrackedButton
      disableAutomaticSvgSizing
      disabled={isLocked}
      onClick={() => void onDeleteEmptySessionClick()}
      variant={'outline'}
      size={'xs'}
      className={cn(
        'flex gap-3 text-text-secondary',
        isSidebarPanelEnabled && 'mx-1 mb-2'
      )}
      actionTrackingName={ButtonAction.SessionsTidyUp}
    >
      <Wand2 size={12} />
      <TypographyCaptionBold>
        <FormattedMessage defaultMessage="Tidy up" id="QSTghu+6XD" />
      </TypographyCaptionBold>
    </TrackedButton>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {isLocked ? (
          // Disabled buttons get no pointer/focus events; the span is the trigger.
          <span tabIndex={0} className="flex flex-col">
            {button}
          </span>
        ) : (
          button
        )}
      </TooltipTrigger>
      <TooltipContent>
        <TooltipArrow />
        <TypographyCaption>
          {isLocked ? (
            lockedReason
          ) : (
            <FormattedMessage
              defaultMessage="Delete all empty sessions"
              id="b0inlIZKny"
            />
          )}
        </TypographyCaption>
      </TooltipContent>
    </Tooltip>
  );
};
